// Employee/hooks/useAppState.tsx
import {useEffect, useState} from "react";
import {endOfMonth, endOfWeek, startOfMonth, startOfWeek} from "date-fns";

export type AppState = {
    initialView: "day" | "week" | "month";
    initialWeekStart: Date;
    initialUserType: "client" | "psychologist" | "observer";
    initialCanEdit: boolean;
    initialAvailableViews: ("day" | "week" | "month")[];
    initialShowButtons: boolean;
    initialDebugging: boolean;
    initialTimeZone: string;
    initialShowHorizontalLines: boolean;
    initialShowVerticalLines: boolean;
    initialShowLineToggles: boolean;
    initialMaxDaysForSelection: number;
    initialShowOnlyEvents: boolean;
    initialFetchMode?: "current-user" | "psychologist";
};

function getStartOfWeek(
    date: Date,
    weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6 = 1
): Date {
    return startOfWeek(date, {weekStartsOn});
}

export function useAppState(initialState: AppState) {
    // Retrieve from localStorage or default to initial state
    const getInitialState = (key: string, fallback: any) => {
        const storedValue = localStorage.getItem(key);
        return storedValue ? JSON.parse(storedValue) : fallback;
    };

    const getInitialTimeZone = () => {
        const storedTimeZone = localStorage.getItem('timeZone');
        if (storedTimeZone) {
            return storedTimeZone;
        }

        // If no stored timezone, use the system default
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    };


    const [currentView, setView] = useState<"day" | "week" | "month">(() =>
        getInitialState("currentView", initialState.initialView)
    );
    const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() =>
        getStartOfWeek(new Date())
    );
    const [userType, setUserType] = useState<
        "client" | "psychologist" | "observer"
    >(() => getInitialState("userType", initialState.initialUserType));
    const [canEdit, setCanEdit] = useState<boolean>(() =>
        getInitialState("canEdit", initialState.initialCanEdit)
    );
    const [availableViews, setAvailableViews] = useState(
        initialState.initialAvailableViews
    );
    const [showButtons, setShowButtons] = useState(() =>
        getInitialState("showButtons", initialState.initialShowButtons)
    );
    const [debugging, setDebugging] = useState(() =>
        getInitialState("debugging", initialState.initialDebugging)
    );
    const [timeZone, setTimeZone] = useState<string>(getInitialTimeZone());
    const [refetchTrigger, setRefetchTrigger] = useState(0);
    const [showHorizontalLines, setShowHorizontalLines] = useState(() =>
        getInitialState(
            "showHorizontalLines",
            initialState.initialShowHorizontalLines
        )
    );
    const [showVerticalLines, setShowVerticalLines] = useState(() =>
        getInitialState("showVerticalLines", initialState.initialShowVerticalLines)
    );
    const [showLineToggles, setShowLineToggles] = useState(() =>
        getInitialState("showLineToggles", initialState.initialShowLineToggles)
    );
    const [maxDaysForSelection, setMaxDaysForSelection] = useState(
        initialState.initialMaxDaysForSelection
    );
    const [showOnlyEvents, setShowOnlyEvents] = useState(() =>
        getInitialState("showOnlyEvents", initialState.initialShowOnlyEvents)
    );

    const [fetchMode, setFetchMode] = useState<"current-user" | "psychologist">(
        () =>
            getInitialState(
                "fetchMode",
                initialState.initialFetchMode || "current-user"
            )
    );


    const setTimeZoneAndRefetch = (newTimeZone: string) => {
        if (timeZone !== newTimeZone) {  // Only update if different
            setTimeZone(newTimeZone);
            localStorage.setItem('timeZone', newTimeZone);
            setRefetchTrigger((prev) => prev + 1);
        }
    };

    const [currentViewRange, setCurrentViewRange] = useState<{
        start: Date;
        end: Date;
    }>(() => {
        const now = new Date();
        switch (initialState.initialView) {
            case "day":
                return {start: now, end: now};
            case "week":
                const mondayStart = getStartOfWeek(now, 1);
                return {
                    start: mondayStart,
                    end: endOfWeek(mondayStart, {weekStartsOn: 1}),
                };
            case "month":
                return {start: startOfMonth(now), end: endOfMonth(now)};
        }
    });

    // Save to localStorage on state change

    useEffect(() => {
        localStorage.setItem("showButtons", JSON.stringify(showButtons));
    }, [showButtons]);
    useEffect(() => {
        localStorage.setItem("timeZone", timeZone);
    }, [timeZone]);

    useEffect(() => {
        updateViewRange(currentView, currentWeekStart);
    }, [currentView, currentWeekStart]);

    const updateViewRange = (view: "day" | "week" | "month", date: Date) => {
        switch (view) {
            case "day":
                setCurrentViewRange({start: date, end: date});
                break;
            case "week":
                setCurrentViewRange({
                    start: getStartOfWeek(date),
                    end: endOfWeek(date, {weekStartsOn: 1}),
                });
                break;
            case "month":
                setCurrentViewRange({
                    start: startOfMonth(date),
                    end: endOfMonth(date),
                });
                break;
        }
    };

    const toggleUserType = () => {
        setUserType((prevType) => {
            switch (prevType) {
                case "client":
                    setCanEdit(true);
                    return "psychologist";
                case "psychologist":
                    setCanEdit(false);
                    return "observer";
                case "observer":
                    setCanEdit(false);
                    return "client";
            }
        });
    };

    return {
        currentView,
        setView,
        currentWeekStart,
        setCurrentWeekStart,
        userType,
        toggleUserType,
        canEdit,
        availableViews,
        setAvailableViews,
        showButtons,
        setShowButtons,
        debugging,
        setDebugging,
        timeZone,
        setTimeZone: setTimeZoneAndRefetch,
        showHorizontalLines,
        setShowHorizontalLines,
        showVerticalLines,
        setShowVerticalLines,
        showLineToggles,
        setShowLineToggles,
        currentViewRange,
        updateViewRange,
        maxDaysForSelection,
        setMaxDaysForSelection,
        showOnlyEvents,
        setShowOnlyEvents,
        fetchMode,
        setFetchMode,
        refetchTrigger,
    };
}
